import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { DotCluster } from '@/components/brand/EchoArc'
import { DEMO_SEGMENTS } from '@/lib/demo/data'
import { cn } from '@/lib/utils'
import type { EchoReadiness } from '@/lib/types'

interface Project {
  project_id: string
  name: string
  industry: string
  status: string
}

interface UploadSummary {
  upload_id: string
  filename: string
  row_count: number
  echo_readiness: string
  created_at: number
}

interface ColumnInfo {
  column_name: string
  category: string
  sample_values: string
}

interface PreReadSummary {
  pre_read_id: string
  research_question: string
  simulated_only: number
  created_at: number
  result: string  // JSON string of SegmentAggregate[]
}

const INSURANCE_MATRIX = [
  { name: 'General Homeowners', scores: ['High', 'Medium', 'Low', 'High'] },
  { name: 'Recent Claimants', scores: ['Medium', 'Low', 'High', 'Medium'] },
  { name: 'Price-Sensitive Renters', scores: ['Low', 'High', 'Low', 'Medium'] },
]

const MATRIX_HEADERS = ['Trust language', 'Price sensitivity', 'Claims experience', 'Renewal intent']

const CATEGORY_COLORS: Record<string, string> = {
  respondent_id: 'bg-evidence-blue/10 text-evidence-blue',
  demographic: 'bg-purple-50 text-purple-700',
  prediction: 'bg-human-teal/10 text-human-teal',
  open_ended: 'bg-signal-cyan/10 text-signal-cyan',
  high_frequency: 'bg-orange-50 text-orange-700',
  numerical: 'bg-slate-50 text-slate-600',
  outcome: 'bg-validation-green/10 text-validation-green',
  ignore: 'bg-soft-panel text-slate-400',
}

export function EchoPanelPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === 'demo'

  const [project, setProject] = useState<Project | null>(null)
  const [uploads, setUploads] = useState<UploadSummary[]>([])
  const [readiness, setReadiness] = useState<EchoReadiness | null>(null)
  const [columns, setColumns] = useState<ColumnInfo[]>([])
  const [preReads, setPreReads] = useState<PreReadSummary[]>([])
  const [realSegments, setRealSegments] = useState<{ segment_id: string; segment_name: string; source_column: string | null; twin_count: number; evidence_strength: string }[]>([])
  const [loading, setLoading] = useState(!isDemo)

  useEffect(() => {
    if (isDemo) return
    const token = localStorage.getItem('echo_session')
    const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

    Promise.all([
      fetch(`/api/projects/${projectId}`, { headers: authHeaders })
        .then(r => r.ok ? r.json() as Promise<Project> : null),
      fetch(`/api/projects/${projectId}/uploads`, { headers: authHeaders })
        .then(r => r.ok ? r.json() as Promise<UploadSummary[]> : ([] as UploadSummary[])),
      fetch(`/api/projects/${projectId}/pre-reads`, { headers: authHeaders })
        .then(r => r.ok ? r.json() as Promise<PreReadSummary[]> : ([] as PreReadSummary[])),
    ]).then(([proj, allUploads, prs]) => {
      if (proj) setProject(proj)
      const list = allUploads ?? []
      setUploads(list)
      // Use readiness from the most recent upload that has it
      for (const u of list) {
        try {
          const r = JSON.parse(u.echo_readiness) as EchoReadiness
          if (r?.overall) { setReadiness(r); break }
        } catch { /* keep looking */ }
      }
      setPreReads(prs ?? [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [projectId, isDemo])

  // Fetch column details and segments (uses latest upload server-side)
  useEffect(() => {
    if (isDemo || uploads.length === 0) return
    const token = localStorage.getItem('echo_session')
    const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

    fetch(`/api/projects/${projectId}/data`, { headers: authHeaders })
      .then(r => r.ok ? r.json() as Promise<{ columns?: ColumnInfo[] }> : null)
      .then(d => { if (d?.columns) setColumns(d.columns) })
      .catch(() => {})

    fetch(`/api/projects/${projectId}/segments`, { headers: authHeaders })
      .then(r => r.ok ? r.json() as Promise<{ segments: typeof realSegments }> : null)
      .then(d => { if (d?.segments?.length) setRealSegments(d.segments) })
      .catch(() => {})
  }, [projectId, isDemo, uploads.length])

  const displayName = isDemo
    ? 'Insurance Renewal Message Test'
    : (project?.name ?? (loading ? 'Loading…' : 'New Project'))

  const preReadPath = `/app/projects/${isDemo ? 'demo' : projectId}/pre-reads/new`
  const uploadPath = `/app/projects/${projectId}/upload`
  const latestUpload = uploads[0] ?? null
  const totalRows = uploads.reduce((sum, u) => sum + (u.row_count ?? 0), 0)

  // Derive echo modes from readiness
  const availableModes = readiness?.available_modes ?? (isDemo ? ['study_memory', 'segment_echo', 'validated_echo_panel'] : [])

  return (
    <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Echo Panel</h1>
          <p className="text-sm text-slate-500 mt-1">
            {displayName}
            {isDemo && <> · 3 segments · <Badge variant="success" size="sm">Ready</Badge></>}
            {!isDemo && project && readiness && (
              <> · <span className="capitalize">{project.industry.replace(/-/g, ' ')}</span> · <Badge variant={readiness.overall === 'High' ? 'success' : readiness.overall === 'Medium' ? 'warning' : 'default'} size="sm">{readiness.overall}</Badge></>
            )}
            {!isDemo && project && !readiness && !loading && (
              <> · <span className="capitalize">{project.industry.replace(/-/g, ' ')}</span></>
            )}
          </p>
        </div>
        <Link to={preReadPath}>
          <Button variant="primary">Ask a new question →</Button>
        </Link>
      </div>

      {/* Demo: evidence coverage matrix */}
      {isDemo && (
        <Card className="overflow-x-auto">
          <h3 className="text-sm font-medium text-deep-ink mb-4">Evidence coverage matrix</h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-gray text-slate-400 text-left">
                <th className="pb-2 pr-4 font-medium">Segment</th>
                {MATRIX_HEADERS.map(h => (
                  <th key={h} className="pb-2 pr-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-gray">
              {INSURANCE_MATRIX.map(row => (
                <tr key={row.name}>
                  <td className="py-2.5 pr-4 text-slate-600 font-medium">{row.name}</td>
                  {row.scores.map((score, i) => (
                    <td key={i} className="py-2.5 pr-4">
                      <Badge
                        variant={score === 'High' ? 'success' : score === 'Medium' ? 'warning' : 'danger'}
                        size="sm"
                      >{score}</Badge>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Real project: loading */}
      {!isDemo && loading && (
        <Card className="bg-off-white border-0">
          <div className="text-center py-8">
            <p className="text-sm text-slate-400">Loading project data…</p>
          </div>
        </Card>
      )}

      {/* Real project with upload data */}
      {!isDemo && !loading && uploads.length > 0 && (
        <>
          {/* Upload summary + readiness */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="space-y-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-medium text-deep-ink">Study data</h3>
                <Link to={uploadPath}>
                  <Button variant="ghost" size="sm" className="text-xs">+ Add file</Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-2xl font-medium text-deep-ink">{totalRows.toLocaleString()}</p>
                  <p className="text-xs text-slate-400">total respondents</p>
                </div>
                <div>
                  <p className="text-2xl font-medium text-deep-ink">{columns.length || '—'}</p>
                  <p className="text-xs text-slate-400">columns mapped</p>
                </div>
              </div>
              {/* File list */}
              <div className="space-y-2 pt-1 border-t border-border-gray">
                {uploads.map(u => (
                  <div key={u.upload_id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-mono text-slate-600 truncate">{u.filename}</p>
                      <p className="text-xs text-slate-400">{u.row_count?.toLocaleString() ?? '—'} rows · {new Date(u.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge variant="success" size="sm">Uploaded</Badge>
                  </div>
                ))}
              </div>
            </Card>

            {readiness && (
              <Card className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-deep-ink">Echo Readiness</h3>
                  <Badge variant={readiness.overall === 'High' ? 'success' : readiness.overall === 'Medium' ? 'warning' : 'danger'}>
                    {readiness.overall}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'Response volume', value: readiness.response_volume },
                    { label: 'Open-ended depth', value: readiness.open_ended_depth },
                    { label: 'Segment coverage', value: readiness.segment_coverage },
                    { label: 'Source diversity', value: readiness.source_diversity },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-32 shrink-0">{item.label}</span>
                      <div className="flex-1 h-1.5 bg-soft-panel rounded-full">
                        <div
                          className={cn('h-full rounded-full', item.value === 'High' ? 'bg-validation-green' : item.value === 'Medium' ? 'bg-uncertainty-amber' : 'bg-slate-300')}
                          style={{ width: item.value === 'High' ? '85%' : item.value === 'Medium' ? '55%' : '25%' }}
                        />
                      </div>
                      <Badge variant={item.value === 'High' ? 'success' : item.value === 'Medium' ? 'warning' : 'danger'} size="sm">
                        {item.value}
                      </Badge>
                    </div>
                  ))}
                </div>
                {readiness.limitations && readiness.limitations.length > 0 && (
                  <div className="pt-2 border-t border-border-gray space-y-1">
                    {readiness.limitations.map((lim, i) => (
                      <p key={i} className="text-xs text-slate-400 italic">{lim}</p>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Column map */}
          {columns.length > 0 && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-deep-ink">Column map</h3>
                <Link to={`/app/projects/${projectId}/data`}>
                  <Button variant="ghost" size="sm" className="text-xs">View full data →</Button>
                </Link>
              </div>
              <div className="flex flex-wrap gap-2">
                {columns.map(col => (
                  <div
                    key={col.column_name}
                    className={cn('flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border border-transparent', CATEGORY_COLORS[col.category] ?? 'bg-soft-panel text-slate-500')}
                  >
                    <span className="font-mono font-medium">{col.column_name}</span>
                    <span className="opacity-60">·</span>
                    <span className="capitalize">{col.category.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Real project, no upload yet */}
      {!isDemo && !loading && uploads.length === 0 && (
        <Card className="bg-off-white border-0">
          <div className="text-center py-8 space-y-3">
            <p className="text-sm font-medium text-deep-ink">No study data yet</p>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Upload a CSV to map your audience segments and compute Echo Readiness.
            </p>
            <div className="flex gap-3 justify-center pt-2">
              <Link to={uploadPath}>
                <Button variant="primary" size="sm">Upload data →</Button>
              </Link>
              <Link to={preReadPath}>
                <Button variant="outline" size="sm">Run pre-read anyway</Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {/* Question history — real projects only */}
      {!isDemo && !loading && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-medium text-deep-ink">Question history</h2>
            <Link to={preReadPath}>
              <Button variant="outline" size="sm">+ New question</Button>
            </Link>
          </div>

          {preReads.length === 0 ? (
            <Card className="text-center py-8 space-y-2 bg-off-white border-0">
              <p className="text-sm text-slate-500">No questions asked yet.</p>
              <Link to={preReadPath}>
                <Button variant="primary" size="sm">Ask your first question →</Button>
              </Link>
            </Card>
          ) : (
            <div className="space-y-3">
              {preReads.map(pr => {
                let aggregates: Array<{ segment_name: string; predicted_winner: string; confidence?: { band: string } }> = []
                try { aggregates = JSON.parse(pr.result) } catch { }

                return (
                  <Card key={pr.pre_read_id} className="space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-deep-ink truncate">"{pr.research_question}"</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(pr.created_at).toLocaleString()}
                          <span className="ml-2">
                            <Badge variant={pr.simulated_only ? 'demo' : 'success'} size="sm">
                              {pr.simulated_only ? 'Simulated' : 'Live LLM'}
                            </Badge>
                          </span>
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Link to={`/app/projects/${projectId}/validation`}>
                          <Button variant="outline" size="sm">Validate</Button>
                        </Link>
                        <Link to={`/app/projects/${projectId}/report`}>
                          <Button variant="primary" size="sm">Report →</Button>
                        </Link>
                      </div>
                    </div>

                    {aggregates.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1 border-t border-border-gray">
                        {aggregates.map(agg => (
                          <div key={agg.segment_name} className="flex items-center gap-1.5 text-xs bg-soft-panel rounded-lg px-2.5 py-1.5">
                            <span className="text-slate-500">{agg.segment_name}</span>
                            <span className="text-border-gray">→</span>
                            <span className="font-medium text-human-teal">{agg.predicted_winner}</span>
                            {agg.confidence && (
                              <Badge
                                variant={agg.confidence.band === 'High' ? 'success' : agg.confidence.band === 'Medium' ? 'warning' : 'danger'}
                                size="sm"
                              >{agg.confidence.band}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Echo modes */}
      <Card className="bg-off-white border-0">
        <h3 className="text-sm font-medium text-deep-ink mb-3">
          Available echo modes
          {!isDemo && uploads.length === 0 && <span className="text-slate-400 font-normal ml-1">(upload data to unlock)</span>}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            {
              mode: 'Study Memory',
              key: 'study_memory',
              desc: 'Always available. Structured retrieval over uploaded evidence.',
              alwaysAvailable: true,
            },
            {
              mode: 'Segment Echo',
              key: 'segment_echo',
              desc: isDemo ? 'Medium+ readiness. Segment-level directional predictions.' : (uploads.length > 0 ? 'Available with demographic columns in your upload.' : 'Upload data with segment identifiers to unlock.'),
              alwaysAvailable: false,
            },
            {
              mode: 'Respondent Echo',
              key: 'respondent_echo',
              desc: 'Requires repeated respondent IDs (≥30 rows/respondent).',
              alwaysAvailable: false,
            },
            {
              mode: 'Validated Echo Panel',
              key: 'validated_echo_panel',
              desc: isDemo ? 'Outcome column present. Available after first validation round.' : (readiness?.outcome_present ? 'Run a validation study to unlock.' : 'Requires an outcome column in your data.'),
              alwaysAvailable: false,
            },
          ].map(m => {
            const available = m.alwaysAvailable || availableModes.includes(m.key)
            return (
              <div key={m.mode} className="flex items-start gap-2 text-sm">
                <span className={available ? 'text-validation-green' : 'text-border-gray'}>
                  {available ? '✓' : '○'}
                </span>
                <div>
                  <span className={cn('font-medium', !available && 'text-slate-400')}>{m.mode}</span>
                  <p className="text-xs text-slate-400 mt-0.5">{m.desc}</p>
                </div>
              </div>
            )
          })}
        </div>
      </Card>

      {/* Demo segment cards */}
      {isDemo && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {DEMO_SEGMENTS.map(seg => (
            <Card key={seg.segment_id} className="space-y-4">
              <div className="flex items-start gap-3">
                <DotCluster
                  count={Math.min(Math.ceil(seg.respondent_count / 55), 20)}
                  size={48}
                  color={seg.evidence_strength === 'High' ? '#1E2D5A' : seg.evidence_strength === 'Medium' ? '#A36B1F' : '#9B2226'}
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-deep-ink">{seg.name}</p>
                  <p className="text-xs text-slate-400">{seg.respondent_count.toLocaleString()} respondents</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Evidence</span>
                <Badge
                  variant={seg.evidence_strength === 'High' ? 'success' : seg.evidence_strength === 'Medium' ? 'warning' : 'danger'}
                  size="sm"
                >{seg.evidence_strength}</Badge>
                {seg.evidence_strength === 'Low' && (
                  <span className="text-xs text-uncertainty-amber">⚠ Validate this segment</span>
                )}
              </div>

              {seg.demographic_skew && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-400">Demographics</p>
                  {Object.entries(seg.demographic_skew).slice(0, 3).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">{k.replace(/_/g, ' ')}</span>
                      <span className="text-slate-700 font-medium">{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              {seg.dominant_themes && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-400">Dominant themes</p>
                  {seg.dominant_themes.map((theme: string) => (
                    <p key={theme} className="text-xs text-slate-600 flex items-start gap-1">
                      <span className="text-human-teal shrink-0 mt-0.5">·</span>{theme}
                    </p>
                  ))}
                </div>
              )}

              {seg.representative_quotes && seg.representative_quotes.length > 0 && (
                <div className="pt-3 border-t border-border-gray space-y-2">
                  <p className="text-xs text-slate-400">Representative quotes</p>
                  {seg.representative_quotes.slice(0, 2).map((q, i) => (
                    <blockquote key={i} className="text-xs italic text-slate-500 border-l-2 border-human-teal/30 pl-2">
                      "{q.text.slice(0, 120)}{q.text.length > 120 ? '…' : ''}"
                      <cite className="not-italic text-slate-400 block mt-0.5">
                        {q.source_file}, row {q.source_row}
                      </cite>
                    </blockquote>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Real segments from uploaded data */}
      {!isDemo && realSegments.length > 0 && (
        <div>
          <p className="text-xs text-slate-400 mb-3">
            Segments from your data
            {realSegments[0]?.source_column && (
              <span className="ml-1 text-slate-300">· derived from <span className="font-medium text-slate-500">{realSegments[0].source_column}</span></span>
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {realSegments.slice(0, 6).map(seg => (
              <Card key={seg.segment_id} className="space-y-3">
                <div className="flex items-start gap-3">
                  <DotCluster
                    count={Math.min(Math.ceil(seg.twin_count / 20), 20)}
                    size={40}
                    color={seg.evidence_strength === 'High' ? '#1E2D5A' : seg.evidence_strength === 'Medium' ? '#A36B1F' : '#9B2226'}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-deep-ink">{seg.segment_name}</p>
                    <p className="text-xs text-slate-400">{seg.twin_count.toLocaleString()} respondents</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Evidence</span>
                  <Badge
                    variant={seg.evidence_strength === 'High' ? 'success' : seg.evidence_strength === 'Medium' ? 'warning' : 'danger'}
                    size="sm"
                  >{seg.evidence_strength}</Badge>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {(isDemo || (uploads.length > 0 && !loading)) && (
        <div className="flex justify-end">
          <Link to={preReadPath}>
            <Button variant="primary" size="lg">Ask a new question →</Button>
          </Link>
        </div>
      )}
    </div>
  )
}
